import { Link } from 'react-router-dom';
import { Check, X, ArrowRight, Shield, Award, AlertTriangle, HelpCircle, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import SEO from '../components/SEO';

const registrationTiers = [
  { name: 'SILVER', color: 'bg-gray-400', recommended: false },
  { name: 'GOLD', color: 'bg-yellow-500', recommended: false },
  { name: 'DIAMOND', color: 'bg-cyan-400', recommended: false },
  { name: 'PLATINUM', color: 'bg-purple-500', recommended: true },
  { name: 'TITANIUM', color: 'bg-amber-600', recommended: false },
];

const pricingData = [
  { feature: 'Early Bird Entry Price', silver: 'USD 250', gold: 'USD 570', diamond: 'USD 1,030', platinum: 'USD 1,280', titanium: 'USD 1,650' },
  { feature: 'Regular Entry Price', silver: 'USD 299', gold: 'USD 670', diamond: 'USD 1,220', platinum: 'USD 1,490', titanium: 'USD 1,950' },
  { feature: 'Last Minute Entry Price', silver: 'USD 360', gold: 'USD 800', diamond: 'USD 1,460', platinum: 'USD 1,780', titanium: 'USD 2,340' },
  { feature: 'Total Award Participation', silver: '1 Award', gold: '1 Award', diamond: '2 Awards', platinum: '2 Awards', titanium: '3 Awards' },
  { feature: 'Number of Categories (per award)', silver: '1 Category', gold: '2 Categories', diamond: '2 Categories', platinum: '3 Categories', titanium: '3 Categories' },
  { feature: 'Total Nominations', silver: '1 Nomination', gold: '1 Nomination', diamond: '2 Nominations', platinum: '2 Nominations', titanium: '3 Nominations' },
  { feature: 'Competition Level', silver: 'City, Country', gold: 'City, Country', diamond: 'City, Country', platinum: 'City, Country, Regional', titanium: 'City, Country, Regional, World' },
  { feature: 'Featured in Nominees Section', silver: true, gold: true, diamond: true, platinum: true, titanium: true },
  { feature: 'Rights to Use Award Logo', silver: true, gold: true, diamond: true, platinum: true, titanium: true },
  { feature: 'Complimentary Digital Winner Package', silver: false, gold: false, diamond: false, platinum: false, titanium: 'For 1 Win' },
  { feature: 'Link to Website & Social Media', silver: true, gold: true, diamond: true, platinum: true, titanium: true },
  { feature: 'Recommendation for Voting', silver: false, gold: false, diamond: false, platinum: true, titanium: true },
];

const faqs = [
  {
    question: 'Can I nominate my Company individual?',
    answer: 'Yes, we have various awards for individuals in different industries such as International GM Awards for GMs, World MarCom Awards for Marketing Directors, Communication Directors, or any Marketing Team member.',
  },
  {
    question: 'Can I change my nomination category after nomination?',
    answer: 'Category changes are possible before the voting stage begins. Please contact our support team to request a category change.',
  },
  {
    question: 'How many categories can I nominate?',
    answer: 'The number of categories depends on your chosen package. Silver allows 1 category, Gold allows 2, Diamond allows 2, Platinum allows 3, and Titanium allows 3 categories per award.',
  },
  {
    question: 'Do you consider the company size during jury evaluation?',
    answer: 'Yes, our jury evaluation takes into account the company size and evaluates nominations within appropriate peer groups to ensure fair comparison.',
  },
  {
    question: 'Can I do Multiple Nomination in my account?',
    answer: 'Yes, you can submit multiple nominations from the same account. Each nomination will be evaluated independently by our jury panel.',
  },
  {
    question: 'What is a winner package?',
    answer: 'A winner package includes promotional materials, trophies, certificates, and marketing opportunities available exclusively to award winners to maximize the value of their achievement.',
  },
  {
    question: 'What is the Fee to Nominate or Register in the Award Program?',
    answer: 'The registration fee varies by package tier, starting from USD 250 for Silver to USD 1,650 for Titanium (Early Bird pricing). See the pricing table above for complete details.',
  },
  {
    question: 'Can I use Award Logo without buying Winner Package?',
    answer: 'No, exclusive rights to use the award logo and brand name are included only in the winner packages. Please refer to the terms and conditions for details.',
  },
];

export const RegistrationFeePage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const renderCell = (value) => {
    if (value === true) return <Check className="w-5 h-5 text-green-500 mx-auto" />;
    if (value === false) return <X className="w-5 h-5 text-red-500 mx-auto" />;
    return <span className="text-white text-sm">{value}</span>;
  };

  return (
    <main data-testid="registration-fee-page" className="pt-24 min-h-screen bg-[#0a0a0a]">
      <SEO 
        title="Registration Fee - Award Nomination Pricing & Packages"
        description="View Golden Tree Events registration fees for award nominations. Choose from Silver, Gold, Diamond, Platinum, and Titanium packages with early bird discounts. Includes pricing, benefits comparison, and FAQs."
      />
      {/* Hero Section */}
      <section className="py-16 md:py-20 border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/10 via-transparent to-[#D4AF37]/10" />
        <div className="w-full px-4 sm:px-8 lg:px-16 relative z-10">
          <div className="text-center">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Fee / Cost Involved</p>
            <h1 className="font-montserrat text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              Award Nomination <span className="gradient-gold">Fees</span>
            </h1>
            <p className="font-google-sans text-[#A1A1AA] text-base max-w-3xl mx-auto leading-relaxed">
              Our nomination plans are designed to be transparent, fair, and merit-based. Each package provides 
              structured evaluation, global exposure, and equal opportunity—without guarantees, influence, or hidden costs.
            </p>
          </div>
        </div>
      </section>

      {/* Guarantee Cards */}
      <section className="py-16">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Money Back Guarantee */}
            <div className="p-8 bg-green-500/10 border border-green-500/30">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-8 h-8 text-green-500" />
                <h3 className="font-montserrat text-white text-2xl">100% Money-Back Guarantee</h3>
              </div>
              <ul className="space-y-3 text-[#A1A1AA] text-sm">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  If you do not win in any award, we refund your registration money OR apply it toward next year's nomination.
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  You can withdraw your nomination before the voting stage and carry it forward for next year's awards.
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  The nomination fee covers: Registration & profile setup, Award platform access, Jury review preparation, Voting materials & platform usage.
                </li>
              </ul>
            </div>

            {/* No Guarantee on Winning */}
            <div className="p-8 bg-amber-500/10 border border-amber-500/30">
              <div className="flex items-center gap-3 mb-6">
                <Award className="w-8 h-8 text-amber-500" />
                <h3 className="font-montserrat text-white text-2xl">100% Merit-Based Selection</h3>
              </div>
              <ul className="space-y-3 text-[#A1A1AA] text-sm">
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                  We do not accept sponsors in exchange for award wins.
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                  There is no guarantee of winning, irrespective of the nomination plan chosen.
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                  Each nomination goes through: First-level jury review, Public voting, Final jury evaluation.
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                  Any attempt to influence results through money or gifts will lead to immediate disqualification.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Table */}
      <section className="py-16 bg-[#121212]">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white mb-4">
              Award Nomination or <span className="gradient-gold">Registration Fee</span>
            </h2>
          </div>

          {/* Table */}
          <div className="relative">
            <table className="w-full border-collapse">
              <thead>
                <tr className="sticky top-[72px] z-30">
                  <th className="text-left p-4 bg-[#1a1a1a] text-white font-google-sans text-sm uppercase tracking-wider border border-white/10">
                    Features
                  </th>
                  {registrationTiers.map((tier) => (
                    <th
                      key={tier.name}
                      className={`p-4 text-center border border-white/10 ${
                        tier.recommended ? 'bg-[#3d3419]' : 'bg-[#1a1a1a]'
                      }`}
                    >
                      {tier.recommended && (
                        <span className="bg-red-500 text-white text-xs px-3 py-1 uppercase tracking-wider block mb-2">
                          Recommended
                        </span>
                      )}
                      <span className={`inline-block px-3 py-1 text-black text-sm font-bold uppercase ${tier.color}`}>
                        {tier.name}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pricingData.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-[#0f0f0f]' : 'bg-[#151515]'}>
                    <td className="p-4 text-[#A1A1AA] text-sm border border-white/10 font-medium">
                      {row.feature}
                    </td>
                    <td className="p-4 text-center border border-white/10">{renderCell(row.silver)}</td>
                    <td className="p-4 text-center border border-white/10">{renderCell(row.gold)}</td>
                    <td className="p-4 text-center border border-white/10">{renderCell(row.diamond)}</td>
                    <td className="p-4 text-center border border-white/10 bg-[#D4AF37]/5">{renderCell(row.platinum)}</td>
                    <td className="p-4 text-center border border-white/10">{renderCell(row.titanium)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <a
              href="https://goldentreeawards.com/award-nomination"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="register-awards-btn"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-medium px-10 py-4 uppercase tracking-widest text-sm hover:bg-[#C5A028] transition-all duration-300"
            >
              Register for Awards
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2070&auto=format&fit=crop"
            alt="Awards"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 w-full px-4 sm:px-8 lg:px-16 text-center">
          <h2 className="font-montserrat text-3xl sm:text-4xl text-white mb-4">
            Showcase Your <span className="gradient-gold">Excellence</span>
          </h2>
          <p className="font-google-sans text-[#A1A1AA] mb-8 max-w-2xl mx-auto">
            Be acknowledged for your achievements through one of the world's most respected award platforms.
          </p>
          <a
            href="https://goldentreeawards.com/award-nomination"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-medium px-10 py-4 uppercase tracking-widest text-sm hover:bg-[#C5A028] transition-all duration-300"
          >
            Nominate Now
          </a>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center mb-12">
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">FAQ</p>
            <h2 className="font-montserrat text-3xl sm:text-4xl text-white">
              Frequently Asked <span className="gradient-gold">Questions</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-white/10 bg-white/[0.02]"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left"
                  data-testid={`faq-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="font-google-sans text-white font-medium">{faq.question}</span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-[#D4AF37] transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-40 pb-6' : 'max-h-0'}`}>
                  <p className="px-6 pl-14 text-[#A1A1AA] text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* View Winner Package Link */}
      <section className="py-12 border-t border-white/10">
        <div className="w-full px-4 sm:px-8 lg:px-16 text-center">
          <Link
            to="/winner-package"
            data-testid="view-winner-package"
            className="inline-flex items-center gap-2 text-[#D4AF37] font-medium uppercase tracking-wider hover:gap-3 transition-all duration-300"
          >
            View Winner Package Pricing
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default RegistrationFeePage;
